import React, {FC, ReactNode, useCallback, useMemo, useRef, useState} from 'react';
import root from 'window-or-global';

import StatusSnackbar, {
    Statuses
} from '../../../../../pages/fortnite-games/components/StatusSnackbar';
import useTrackExportErrors from '../export/useTrackExportErrors';

export type CSVContent = [string, () => string];

export interface BulkExportContextValue {
    registerCSVContent: (content: CSVContent) => void;
    unregisterCSVContent: (content: CSVContent) => void;
    onDownload: (fileName: string) => void;
}

export interface BulkExportProviderProps {
    children: ReactNode;
}

const prepareFileName = (name: string) => name.replace(/\//g, '_');
const download = (fileName: string, base64: string) => {
    const link = root.document.createElement('a');
    link.href = `data:application/zip;base64,${base64}`;
    link.download = `${fileName}.zip`;

    link.click();
    link.remove();
};

export const CSVExportContext = React.createContext<BulkExportContextValue>({
    registerCSVContent: () => {},
    unregisterCSVContent: () => {},
    onDownload: () => {}
});

const BulkExportProvider: FC<BulkExportProviderProps> = ({children}: BulkExportProviderProps) => {
    const fileName = useRef<string>('untitled');
    const exporters = useRef(new Set<CSVContent>());
    const [status, setStatus] = useState<Statuses>(Statuses.idle);
    const {trackCSVError} = useTrackExportErrors();

    const registerCSVContent = useCallback((content: CSVContent) => {
        exporters.current.add(content);
    }, []);

    const unregisterCSVContent = useCallback((content: CSVContent) => {
        exporters.current.delete(content);
    }, []);

    const resetError = useCallback(() => setStatus(Statuses.idle), [setStatus]);

    const downloadHandler = useCallback(async (name: string) => {
        if (status === Statuses.loading) {
            return;
        }

        let timeout;

        try {
            fileName.current = name;
            timeout = setTimeout(() => setStatus(Statuses.loading), 300);

            // @ts-ignore
            const {default: JSZip} = await import(/* webpackChunkName: "jszip" */ 'jszip');

            const zip = new JSZip();

            exporters.current.forEach(([fileName, getContent]) => {
                zip.file(prepareFileName(fileName), getContent());
            });

            const base64 = await zip.generateAsync({type: 'base64'});
            download(name, base64);

            setStatus(Statuses.idle);
        } catch (error) {
            setStatus(Statuses.error);
            trackCSVError(error);
        } finally {
            clearTimeout(timeout);
        }
    }, []);

    const handleRetry = () => {
        if (status !== Statuses.loading) {
            resetError();

            // Without timeout it seems like nothing happens
            setTimeout(() => downloadHandler(fileName.current), 500);
        }
    };

    const value = useMemo(
        () => ({
            registerCSVContent,
            unregisterCSVContent,
            onDownload: downloadHandler
        }),
        []
    );

    return (
        <CSVExportContext.Provider value={value}>
            {children}
            <StatusSnackbar
                status={status}
                loadingMessageCode="epic.affiliate.fortnite_games.widget_csv_download.loading"
                onRetry={handleRetry}
                onClose={resetError}
            />
        </CSVExportContext.Provider>
    );
};

export default BulkExportProvider;
